import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Landmark, Check, X, Download, BellRing, Settings as SettingsIcon, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const fmt = (d) => (d ? new Date(d).toLocaleString("fr-BE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const LeadCell = ({ t }) => {
  const leads = t.leads || [];
  if (!leads.length) return <div className="text-xs text-slate-400">—</div>;
  return (
    <div data-testid={`transfer-leads-${t.id}`}>
      {leads.slice(0, 3).map((l, i) => (
        <div key={i} className="text-xs leading-tight">
          <span className="font-mono text-orange-600 font-bold">{l.lead_number}</span>
          <span className="text-slate-500"> · {l.project_type} · {l.city}</span>
        </div>
      ))}
      {leads.length > 3 && <div className="text-[11px] text-slate-400">+{leads.length - 3} autre(s)</div>}
      {t.order_number && <div className="text-[11px] text-slate-400 font-mono mt-0.5">{t.order_number} · {t.lead_count || leads.length} lead(s)</div>}
    </div>
  );
};

export const TransfersTab = ({ API }) => {
  const [rows, setRows] = useState(null);
  const [history, setHistory] = useState(null);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const loadStats = useCallback(() => {
    axios.get(`${API}/admin/transfers/stats`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch(() => setStats(null));
  }, [API]);

  const load = useCallback(() => {
    setRows(null);
    loadStats();
    axios.get(`${API}/admin/pending-transfers`, { withCredentials: true })
      .then((r) => setRows(r.data.transfers || []))
      .catch(() => setRows([]));
  }, [API, loadStats]);

  const loadHistory = useCallback(() => {
    setHistory(null);
    axios.get(`${API}/admin/transfers-history`, { withCredentials: true })
      .then((r) => setHistory(r.data.transfers || []))
      .catch(() => setHistory([]));
  }, [API]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view === "history" && history === null) loadHistory(); }, [view, history, loadHistory]);

  const loadSettings = useCallback(() => {
    axios.get(`${API}/admin/invoice-settings`, { withCredentials: true })
      .then((r) => setSettings(r.data))
      .catch(() => {});
  }, [API]);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const setCompany = (k, v) => setSettings((s) => ({ ...s, company: { ...(s.company || {}), [k]: v } }));
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/invoice-settings`, {
        order_prefix: settings.order_prefix,
        payment_terms_days: settings.payment_terms_days,
        company: settings.company || {},
      }, { withCredentials: true });
      toast.success("Coordonnées du bon de commande enregistrées");
      loadSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingSettings(false);
    }
  };

  const act = async (id, action) => {
    if (action === "reject" && !window.confirm("Refuser ce virement ? Les leads ne seront pas débloqués.")) return;
    if (action === "validate" && !window.confirm("Confirmer la réception du paiement ? Tous les leads de cette commande seront débloqués.")) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/transfers/${id}/${action}`, {}, { withCredentials: true });
      toast.success(action === "validate" ? "Paiement validé, leads débloqués" : "Virement refusé");
      load();
      if (view === "history") loadHistory(); else setHistory(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  const remind = async (id) => {
    if (!window.confirm("Envoyer un rappel de paiement à cette entreprise par e-mail ?")) return;
    setBusyId(id);
    try {
      const { data } = await axios.post(`${API}/admin/transfers/${id}/remind`, {}, { withCredentials: true });
      toast.success(data.message || "Rappel envoyé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="transfers-tab-content">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2"><Landmark className="w-5 h-5 text-orange-500" />Virements bancaires</h2>
          <p className="text-sm text-slate-500">Validez un virement une fois le paiement reçu pour débloquer le lead, ou consultez l'historique.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { load(); loadHistory(); }} data-testid="refresh-transfers"><RefreshCw className="w-4 h-4 mr-2" />Actualiser</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" data-testid="transfers-stats">
        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <p className="text-xs font-medium text-green-700/70 uppercase tracking-wide">Encaissé par virement · {stats?.month_label || "--"}</p>
          <p className="text-2xl font-bold text-green-700 mt-1" data-testid="stats-month-total">{stats ? `${Number(stats.month_total).toFixed(2)}€` : "—"}</p>
          <p className="text-xs text-green-700/70 mt-0.5">{stats ? `${stats.month_count} virement(s) validé(s)` : ""}</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-medium text-amber-700/70 uppercase tracking-wide">En attente de validation</p>
          <p className="text-2xl font-bold text-amber-700 mt-1" data-testid="stats-pending-total">{stats ? `${Number(stats.pending_total).toFixed(2)}€` : "—"}</p>
          <p className="text-xs text-amber-700/70 mt-0.5">{stats ? `${stats.pending_count} en attente` : ""}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total potentiel</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{stats ? `${(Number(stats.month_total) + Number(stats.pending_total)).toFixed(2)}€` : "—"}</p>
          <p className="text-xs text-slate-500 mt-0.5">encaissé + en attente</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setView("pending")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${view === "pending" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="transfers-view-pending">
          En attente{Array.isArray(rows) ? ` (${rows.length})` : ""}
        </button>
        <button onClick={() => setView("history")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${view === "history" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="transfers-view-history">
          Historique{Array.isArray(history) ? ` (${history.length})` : ""}
        </button>
        {view === "history" && Array.isArray(history) && history.length > 0 && (
          <div className="ml-auto flex gap-2">
            <a href={`${API}/admin/transfers/export?fmt=csv`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors" data-testid="export-transfers-csv"><Download className="w-4 h-4" />CSV</a>
            <a href={`${API}/admin/transfers/export?fmt=excel`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors" data-testid="export-transfers-excel"><Download className="w-4 h-4" />Excel</a>
          </div>
        )}
      </div>

      {view === "pending" ? (
        rows === null ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-orange-500" /></div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-slate-400" data-testid="transfers-empty">
            <Landmark className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            Aucun virement en attente de validation.
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Entreprise</TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Communication</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((t) => (
                  <TableRow key={t.id} data-testid={`transfer-row-${t.id}`}>
                    <TableCell className="text-xs text-slate-500">
                      {fmt(t.created_at)}
                      {t.days_pending != null && (
                        <div className={`text-[11px] font-semibold mt-0.5 ${t.days_pending >= 7 ? "text-red-600" : t.days_pending >= 3 ? "text-orange-500" : "text-slate-400"}`}>
                          {t.days_pending === 0 ? "aujourd'hui" : `il y a ${t.days_pending} j`}
                          {t.reminders_sent > 0 && ` · ${t.reminders_sent} relance${t.reminders_sent > 1 ? "s" : ""}`}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">{t.company_name}</div>
                      <div className="text-xs text-slate-500">{t.contractor_email}</div>
                    </TableCell>
                    <TableCell><LeadCell t={t} /></TableCell>
                    <TableCell className="font-semibold text-slate-900">{Number(t.amount).toFixed(2)}€</TableCell>
                    <TableCell className="text-xs text-slate-600">{t.reference}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={busyId === t.id} onClick={() => act(t.id, "validate")} data-testid={`validate-transfer-${t.id}`}>
                          {busyId === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Valider
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={busyId === t.id} onClick={() => act(t.id, "reject")} data-testid={`reject-transfer-${t.id}`}>
                          <X className="w-4 h-4 mr-1" />Refuser
                        </Button>
                        <Button size="sm" variant="outline" className="text-orange-600 border-orange-200 hover:bg-orange-50" disabled={busyId === t.id} onClick={() => remind(t.id)} data-testid={`remind-transfer-${t.id}`} title="Relancer l'entreprise">
                          <BellRing className="w-4 h-4 mr-1" />Relancer
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : (
        history === null ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-orange-500" /></div>
        ) : history.length === 0 ? (
          <div className="py-16 text-center text-slate-400" data-testid="transfers-history-empty">
            <Landmark className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            Aucun virement traité pour le moment.
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Traité le</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Entreprise</TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Communication</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((t) => (
                  <TableRow key={t.id} data-testid={`transfer-history-row-${t.id}`}>
                    <TableCell className="text-xs text-slate-500">{fmt(t.processed_at || t.created_at)}</TableCell>
                    <TableCell>
                      {t.status === "confirmed" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700"><Check className="w-3 h-3" />Validé</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700"><X className="w-3 h-3" />Refusé</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">{t.company_name}</div>
                      <div className="text-xs text-slate-500">{t.contractor_email}</div>
                    </TableCell>
                    <TableCell><LeadCell t={t} /></TableCell>
                    <TableCell className="font-semibold text-slate-900">{Number(t.amount).toFixed(2)}€</TableCell>
                    <TableCell className="text-xs text-slate-600">{t.reference}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      )}

      <div className="mt-8 border-t border-slate-200 pt-5">
        <button onClick={() => setShowSettings((v) => !v)} className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="toggle-order-settings">
          <SettingsIcon className="w-4 h-4" />Coordonnées légales du bon de commande {showSettings ? "▲" : "▼"}
        </button>
        {showSettings && settings && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5 max-w-2xl" data-testid="order-settings-panel">
            <p className="text-xs text-slate-500 mb-4">Ces informations apparaissent sur le PDF du bon de commande. La facture récapitulative est envoyée en fin de mois (e-mail + Peppol).</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["legal_name", "Raison sociale"],
                ["vat_number", "N° TVA"],
                ["address", "Adresse"],
                ["email", "E-mail"],
                ["phone", "Téléphone"],
                ["beneficiary", "Bénéficiaire (virement)"],
                ["iban", "IBAN"],
              ].map(([k, label]) => (
                <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
                  <Input value={(settings.company || {})[k] || ""} onChange={(e) => setCompany(k, e.target.value)} data-testid={`order-company-${k}`} />
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Préfixe bon de commande</label>
                <Input value={settings.order_prefix || ""} onChange={(e) => setSettings((s) => ({ ...s, order_prefix: e.target.value }))} data-testid="order-prefix-input" />
                <p className="text-[11px] text-slate-400 mt-1">Prochain n° : {settings.next_order_preview || "—"}</p>
              </div>
            </div>
            <Button onClick={saveSettings} disabled={savingSettings} className="mt-4 bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-order-settings">
              {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
